<template>
    <v-card tile>
        <v-card-title class="text-uppercase">
            Actual VS Forecasted Times
        </v-card-title>
        <v-row no-gutters>
            <v-col>
                <MixedChart
                    :height="'300px'"
                    :width="'100%'"
                    :chartId="'TimeChart' + response.reportedRunoutDateTime"
                    :chartData="getChartData()"
                    :chartOptions="getChartOptions()"
                />
            </v-col>
            <v-col> </v-col>
        </v-row>
    </v-card>
</template>

<script>
import MixedChart from '../../charts/Mixed.vue';
import { dateUtils } from '../../mixins/dateUtils';

export default {
    name: 'VolumeGraph',
    props: ['moduleColor', 'response'],
    components: {
        MixedChart
    },
    mixins: [dateUtils],
    data() {
        return {};
    },
    methods: {
        // Function to parse a duration (HH:mm:ss) to milliseconds
        parseDuration(durationStr) {
            const [hours, minutes, seconds] = durationStr
                .split(':')
                .map(Number);
            return (hours * 3600 + minutes * 60 + seconds) * 1000;
        },
        estimatedArrivalDateTime(realDurations, averageDurations) {
            const terminalStart = new Date(realDurations.terminalStart);
            const terminalToTerminalDuration = this.parseDuration(
                averageDurations.terminalToTerminal
            );
            const terminalToStationDuration = this.parseDuration(
                averageDurations.terminalToStation
            );
            const estimatedArrival = new Date(
                terminalStart.getTime() +
                    terminalToTerminalDuration +
                    terminalToStationDuration
            );
            return estimatedArrival;
        },
        agregateTimeToDate(datetime, time) {
            const date = new Date(datetime);
            const [hours, minutes, seconds] = time.split(':').map(Number);
            date.setHours(date.getHours() + hours);
            date.setMinutes(date.getMinutes() + minutes);
            date.setSeconds(date.getSeconds() + seconds);
            return date;
        },
        // Calculate the time difference between two dates in hours
        calculateTimeDifference(start, end) {
            const startTime = new Date(start).getTime();
            const endTime = new Date(end).getTime();
            return (endTime - startTime) / (1000 * 60 * 60); // Convert from milliseconds to hours
        },
        // Helper function to format decimal hours into "Xhrs Ymin"
        formatTime(hoursDecimal) {
            const hours = Math.floor(hoursDecimal);
            const minutes = Math.round((hoursDecimal - hours) * 60);
            return `${hours}hrs ${minutes}min`;
        },
        // Helper function to calculate the percentage difference between actual and forecasted
        calculateVariance(actual, forecasted) {
            if (forecasted === 0) return 'N/A'; // Avoid division by zero
            const variance = ((actual - forecasted) / forecasted) * 100;
            return `${variance.toFixed(2)}%`;
        },
        getChartData() {
            const actualTerminalTime = this.calculateTimeDifference(
                this.response.realDurations.terminalStart,
                this.response.realDurations.terminalEnd
            );

            const forecastTerminalTime = this.calculateTimeDifference(
                this.response.realDurations.terminalStart,
                this.agregateTimeToDate(
                    this.response.realDurations.terminalStart,
                    this.response.averageDurations.terminalToTerminal
                )
            );

            const actualCommuteTime = this.calculateTimeDifference(
                this.response.realDurations.terminalEnd,
                this.response.realDurations.stationStart
            );

            const forecastCommuteTime = this.calculateTimeDifference(
                this.response.realDurations.terminalEnd,
                this.agregateTimeToDate(
                    this.response.realDurations.terminalEnd,
                    this.response.averageDurations.terminalToStation
                )
            );

            const actualStationTime = this.calculateTimeDifference(
                this.response.realDurations.stationStart,
                this.response.realDurations.stationEnd
            );

            const forecastStationTime = this.calculateTimeDifference(
                this.response.realDurations.stationStart,
                this.agregateTimeToDate(
                    this.response.realDurations.stationStart,
                    this.response.averageDurations.stationToStation
                )
            );

            return {
                labels: ['Terminal', 'Commute', 'Station'],
                datasets: [
                    {
                        label: 'Actual',
                        type: 'bar',
                        backgroundColor: '#009688',
                        data: [
                            actualTerminalTime,
                            actualCommuteTime,
                            actualStationTime
                        ],
                        barPercentage: 0.5,
                        barThickness: 100,
                        maxBarThickness: 150
                    },
                    {
                        label: 'Forecasted',
                        type: 'bar',
                        backgroundColor: '#ff5722 ',
                        data: [
                            forecastTerminalTime,
                            forecastCommuteTime,
                            forecastStationTime
                        ],
                        barPercentage: 0.5,
                        barThickness: 100,
                        maxBarThickness: 150
                    }
                ]
            };
        },
        getChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'category',
                        title: {
                            display: true,
                            text: 'Comparison Type'
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Time (Hours)' // Display time in hours on the y-axis
                        }
                    }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: tooltipItem => {
                                const index = tooltipItem.dataIndex;
                                const dataset = tooltipItem.dataset;
                                const actualTime =
                                    dataset.label === 'Actual'
                                        ? dataset.data[index]
                                        : tooltipItem.chart.data.datasets[0]
                                              .data[index];
                                const forecastTime =
                                    dataset.label === 'Forecasted'
                                        ? dataset.data[index]
                                        : tooltipItem.chart.data.datasets[1]
                                              .data[index];

                                const timeFormatted = this.formatTime(
                                    tooltipItem.raw
                                );
                                const variance = this.calculateVariance(
                                    actualTime,
                                    forecastTime
                                );

                                return `${dataset.label}: ${timeFormatted} (Difference ${variance})`;
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: 'Time Comparison'
                    }
                }
            };
        }
    }
};
</script>
